const DASHBOARD_MODULE_COUNT = 6;

const toComparableId = (value) =>
  value === null || value === undefined ? null : String(value);

export const isSameId = (left, right) => {
  const normalizedLeft = toComparableId(left);
  const normalizedRight = toComparableId(right);

  return normalizedLeft !== null && normalizedLeft === normalizedRight;
};

const normalizeIdArray = (values = []) => {
  if (!Array.isArray(values)) {
    return [];
  }

  return values
    .map((value) => {
      if (value === null || value === undefined) {
        return null;
      }

      if (typeof value === 'object') {
        return value.userId ?? value.id ?? value.groupId ?? null;
      }

      return toComparableId(value);
    })
    .filter((value) => value !== null && value !== undefined);
};

const normalizeModules = (modules = []) => {
  const modulesByNumber = new Map();

  if (Array.isArray(modules)) {
    modules.forEach((module, index) => {
      if (!module) {
        return;
      }

      const moduleNumber = Number(module.moduleNumber ?? module.module ?? index + 1);

      if (!Number.isInteger(moduleNumber) || moduleNumber < 1) {
        return;
      }

      modulesByNumber.set(moduleNumber, {
        ...module,
        moduleNumber,
        dateCompleted: module.dateCompleted ?? null,
        engagementScore: module.engagementScore ?? null,
      });
    });
  }

  return Array.from({ length: DASHBOARD_MODULE_COUNT }, (_, index) => {
    const moduleNumber = index + 1;

    return (
      modulesByNumber.get(moduleNumber) ?? {
        moduleNumber,
        dateCompleted: null,
        engagementScore: null,
      }
    );
  });
};

const normalizeFacilitators = (facilitators = []) =>
  Array.isArray(facilitators)
    ? facilitators.map((facilitator) => ({
        ...facilitator,
        userId: toComparableId(facilitator?.userId ?? facilitator?.id),
        firstName: facilitator?.firstName ?? null,
        lastName: facilitator?.lastName ?? null,
        completedProgram:
          typeof facilitator?.completedProgram === 'boolean'
            ? facilitator.completedProgram
            : Boolean(facilitator?.programCompleted),
      }))
    : [];

const buildRawGroupEntries = (groups = []) =>
  Array.isArray(groups)
    ? groups.map((group) => ({
        ...group,
        groupId: toComparableId(group?.groupId ?? group?.id),
        parentIds: normalizeIdArray(
          group?.parentIds ?? group?.parents ?? group?.memberIds,
        ),
        facilitatorIds: normalizeIdArray(
          group?.facilitatorIds ?? group?.facilitators,
        ),
      }))
    : [];

const mergeUniqueGroups = (groups = []) => {
  const uniqueGroups = [];

  groups.forEach((group) => {
    if (!group) {
      return;
    }

    const nextGroupId = toComparableId(group.groupId ?? null);
    const nextGroupName = group.groupName ?? null;
    const alreadyIncluded = uniqueGroups.some(
      (existingGroup) =>
        isSameId(existingGroup.groupId, nextGroupId) &&
        existingGroup.groupName === nextGroupName,
    );

    if (alreadyIncluded) {
      return;
    }

    uniqueGroups.push({
      groupId: nextGroupId,
      groupName: nextGroupName,
    });
  });

  return uniqueGroups;
};

const compareText = (left = '', right = '') =>
  String(left ?? '').localeCompare(String(right ?? ''), undefined, {
    sensitivity: 'base',
    numeric: true,
  });

const buildParentGroupLookup = (groups) => {
  const parentGroupLookup = new Map();

  groups.forEach((group) => {
    group.parentIds.forEach((parentId) => {
      const normalizedParentId = toComparableId(parentId);
      const existingGroups = parentGroupLookup.get(normalizedParentId) ?? [];

      parentGroupLookup.set(
        normalizedParentId,
        mergeUniqueGroups([
          ...existingGroups,
          {
            groupId: group.groupId,
            groupName: group.groupName,
          },
        ]),
      );
    });
  });

  return parentGroupLookup;
};

const normalizeParents = (parents = [], parentGroupLookup) =>
  Array.isArray(parents)
    ? parents.map((parent) => {
        const userId = toComparableId(parent?.userId ?? parent?.id);
        const lookupGroups = parentGroupLookup.get(userId) ?? [];
        const explicitGroupId = toComparableId(
          parent?.group?.groupId ?? parent?.groupId ?? null,
        );
        const explicitGroupName =
          parent?.group?.groupName ?? parent?.groupName ?? null;
        const group =
          explicitGroupId !== null || explicitGroupName !== null
            ? {
                groupId: explicitGroupId,
                groupName: explicitGroupName,
              }
            : null;
        const groups = mergeUniqueGroups(
          group ? [group, ...lookupGroups] : lookupGroups,
        );

        return {
          ...parent,
          userId,
          group,
          groups,
          modules: normalizeModules(parent?.modules),
        };
      })
    : [];

const deriveGroupsFromParents = (parents) => {
  const derivedGroups = new Map();

  parents.forEach((parent) => {
    (parent.groups ?? []).forEach((group) => {
      if (!group?.groupId) {
        return;
      }

      const groupKey = toComparableId(group.groupId);
      const existingGroup = derivedGroups.get(groupKey);

      if (existingGroup) {
        existingGroup.parentIds.push(parent.userId);
        return;
      }

      derivedGroups.set(groupKey, {
        groupId: group.groupId,
        groupName: group.groupName,
        parentIds: [parent.userId],
        facilitatorIds: [],
      });
    });
  });

  return Array.from(derivedGroups.values());
};

export const normalizeDashboardData = (dashboardData = {}) => {
  const facilitators = normalizeFacilitators(dashboardData.facilitators);
  const rawGroups = buildRawGroupEntries(dashboardData.groups);
  const parentGroupLookup = buildParentGroupLookup(rawGroups);
  const parents = normalizeParents(dashboardData.parents, parentGroupLookup);
  const groupsSource = rawGroups.length ? rawGroups : deriveGroupsFromParents(parents);

  const groups = groupsSource.map((group) => {
    const parentIds = group.parentIds.length
      ? group.parentIds
      : parents
          .filter((parent) => isSameId(parent.group?.groupId, group.groupId))
          .map((parent) => parent.userId);
    const facilitatorIds = group.facilitatorIds.length
      ? group.facilitatorIds
      : normalizeIdArray(group.facilitators);
    const facilitatorsForGroup = facilitators.filter((facilitator) =>
      facilitatorIds.some((facilitatorId) =>
        isSameId(facilitatorId, facilitator.userId),
      ),
    );

    return {
      ...group,
      parentIds,
      facilitatorIds,
      parentCount: parentIds.length,
      facilitators: facilitatorsForGroup,
    };
  });

  return {
    ...dashboardData,
    orgId: dashboardData.orgId ?? dashboardData.org ?? 'N/A',
    facilitators,
    parents,
    groups,
  };
};

export const getParentGroups = (parent) =>
  mergeUniqueGroups([
    ...(Array.isArray(parent?.groups) ? parent.groups : []),
    ...(parent?.group ? [parent.group] : []),
  ]);

export const getParentGroupIds = (parent) =>
  getParentGroups(parent)
    .map((group) => group.groupId)
    .filter((groupId) => groupId !== null && groupId !== undefined);

export const getParentGroupNames = (parent) =>
  getParentGroups(parent)
    .map((group) => group.groupName)
    .filter(Boolean);

export const hasParentGroup = (parent) => getParentGroups(parent).length > 0;

export const isParentInGroup = (parent, groupId) =>
  getParentGroups(parent).some((group) => isSameId(group.groupId, groupId));

export const getParentGroupSortLabel = (parent) =>
  getParentGroupNames(parent)
    .slice()
    .sort(compareText)
    .join(' | ');

export const hasCompletedFacilitatorProgram = (facilitator) =>
  facilitator?.completedProgram === true;

export const compareParentsByGroupAndName = (firstParent, secondParent) => {
  const firstGroupLabel = getParentGroupSortLabel(firstParent);
  const secondGroupLabel = getParentGroupSortLabel(secondParent);
  const firstHasGroup = Boolean(firstGroupLabel);
  const secondHasGroup = Boolean(secondGroupLabel);
  const firstName = [firstParent?.firstName, firstParent?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const secondName = [secondParent?.firstName, secondParent?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  if (!firstHasGroup && secondHasGroup) return -1;
  if (firstHasGroup && !secondHasGroup) return 1;

  const groupComparison = compareText(firstGroupLabel, secondGroupLabel);

  if (groupComparison !== 0) {
    return groupComparison;
  }

  return compareText(firstName, secondName);
};
